import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  Box,
} from "@mui/material";
import { useSnackbar } from "notistack";
import axios from "axios";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDLoading from "components/MDLoading/MDLoading";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

// Icons
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";

function ControlPanel() {
  const { enqueueSnackbar } = useSnackbar();
  const auth = localStorage.getItem("authToken");
  const userId = localStorage.getItem("user_id") || "1";

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allDevices, setAllDevices] = useState([]);
  const [subDataList, setSubDataList] = useState([]);
  const [groupsList, setGroupsList] = useState([]);

  // Target Type: 'device' or 'group'
  const [targetType, setTargetType] = useState("device");
  const [selectedGroupId, setSelectedGroupId] = useState("");

  // Selected Device Form State
  const [camId, setCamId] = useState("10");
  const [ledR, setLedR] = useState(true); // RED_LED
  const [ledG, setLedG] = useState(true); // GREEN_LED
  const [ledW, setLedW] = useState(true); // WHITE_LED
  const [ledY, setLedY] = useState(true); // YELLOW_LED
  const [deviceState, setDeviceState] = useState("REQUEST_STATE");
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchControlPanelData(false);
    const interval = setInterval(() => {
      fetchControlPanelData(true);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const fetchControlPanelData = async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    let devs = [];
    let subList = [];

    try {
      // 1. Fetch devices list (from device_light table based on user role)
      try {
        const devRes = await axios.post(
          `${process.env.REACT_APP_BASEURL}/admin/device/getalldevice`,
          { user_id: userId },
          { headers: { Authorization: `Bearer ${auth}` } }
        );
        if (devRes.data && Array.isArray(devRes.data.areas)) {
          devs = devRes.data.areas;
          setAllDevices(devs);
        }
      } catch (err) {
        console.error("Error fetching devices for control panel:", err);
      }

      // 2. Fetch telemetry subdata
      try {
        const subRes = await axios.post(
          `${process.env.REACT_APP_BASEURL}/admin/users/getsubdata`,
          { ticket_id: "" },
          { headers: { Authorization: `Bearer ${auth}` } }
        );
        if (subRes.data) {
          subList = Array.isArray(subRes.data)
            ? subRes.data
            : Array.isArray(subRes.data.data)
            ? subRes.data.data
            : [];
          setSubDataList(subList);
        }
      } catch (err) {
        console.error("Error fetching subdata:", err);
      }

      // 3. Fetch groups list
      try {
        const groupsRes = await axios.post(
          `${process.env.REACT_APP_BASEURL}/admin/groups/all`,
          { user_id: userId },
          { headers: { Authorization: `Bearer ${auth}` } }
        );
        if (groupsRes.data && Array.isArray(groupsRes.data.groups)) {
          setGroupsList(groupsRes.data.groups);
          if (groupsRes.data.groups.length > 0) {
            setSelectedGroupId((prev) => prev || String(groupsRes.data.groups[0].group_id));
          }
        }
      } catch (err) {
        console.error("Error fetching groups for control panel:", err);
      }

      // Automatically set initial selected device if needed
      const availableList = devs.length > 0 ? devs : subList;
      if (availableList.length > 0 && (!camId || camId === "10")) {
        const firstId = String(availableList[0].device_id || availableList[0].cam_id || availableList[0].camera_id || "10");
        setCamId(firstId);

        if (!isSilent) {
          const foundSub = subList.find((item) => String(item.cam_id) === firstId);
          if (foundSub) {
            populateDeviceDetails(foundSub);
          } else {
            const foundDev = devs.find((item) => String(item.device_id || item.cam_id) === firstId);
            if (foundDev) {
              const isOn = foundDev.current_status === "1";
              setLedR(isOn);
              setLedG(isOn);
              setLedW(isOn);
              setLedY(isOn);
              setDeviceState("REQUEST_STATE");
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching control panel data:", error);
      if (!isSilent) enqueueSnackbar("Error fetching device status data", { variant: "error" });
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  const populateDeviceDetails = (record) => {
    try {
      const parsed = typeof record.data === "string" ? JSON.parse(record.data) : record.data;
      if (parsed) {
        setLedR(parsed.RED_LED === "ON" || parsed.LED_R === "ON");
        setLedG(parsed.GREEN_LED === "ON" || parsed.LED_G === "ON");
        setLedW(parsed.WHITE_LED === "ON" || parsed.LED_W === "ON");
        setLedY(parsed.YELLOW_LED === "ON" || parsed.LED_Y === "ON");
        if (parsed.State) setDeviceState(parsed.State);
      }
      if (record.updated_at) {
        setLastUpdated(new Date(record.updated_at).toLocaleString());
      }
    } catch (e) {
      console.error("Failed to parse device data JSON", e);
    }
  };

  const handleDeviceChange = (event) => {
    const selectedId = event.target.value;
    setCamId(selectedId);

    const foundSub = (Array.isArray(subDataList) ? subDataList : []).find(
      (item) => String(item.cam_id) === String(selectedId)
    );
    if (foundSub) {
      populateDeviceDetails(foundSub);
    } else {
      const foundDev = (Array.isArray(allDevices) ? allDevices : []).find(
        (item) => String(item.device_id || item.cam_id) === String(selectedId)
      );
      if (foundDev) {
        const isOn = foundDev.current_status === "1";
        setLedR(isOn);
        setLedG(isOn);
        setLedW(isOn);
        setLedY(isOn);
        setDeviceState("REQUEST_STATE");
      }
    }
  };

  const formatSmartlightId = (id) => {
    if (!id) return "001";
    const str = String(id).trim();
    const digits = str.replace(/\D/g, "");
    return digits ? digits.padStart(3, "0") : str;
  };

  const formattedCamId = formatSmartlightId(camId);

  const handleSendLightResponse = async () => {
    setSubmitting(true);
    const isGroup = targetType === "group";

    const payload = {
      ...(isGroup ? { group_id: selectedGroupId } : { cam_id: camId }),
      RED_LED: ledR ? "ON" : "OFF",
      GREEN_LED: ledG ? "ON" : "OFF",
      WHITE_LED: ledW ? "ON" : "OFF",
      YELLOW_LED: ledY ? "ON" : "OFF",
      // Legacy compatibility
      ledr: ledR ? "ON" : "OFF",
      ledg: ledG ? "ON" : "OFF",
      ledw: ledW ? "ON" : "OFF",
      ledy: ledY ? "ON" : "OFF",
      state: deviceState,
      user_id: userId,
    };

    try {
      const endpoint = isGroup
        ? `${process.env.REACT_APP_BASEURL}/admin/groups/senddata`
        : `${process.env.REACT_APP_BASEURL}/admin/users/senddata`;

      const res = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${auth}` },
      });

      if (res.data && res.data.success !== false) {
        if (isGroup) {
          const grp = groupsList.find((g) => String(g.group_id) === String(selectedGroupId));
          enqueueSnackbar(
            `Lighting request sent to all ${res.data.device_count || grp?.devices?.length || ""} smartlights in group "${grp?.group_name || ""}" successfully!`,
            { variant: "success" }
          );
        } else {
          enqueueSnackbar(`Light response sent to Smartlight-${formattedCamId}/pub successfully!`, {
            variant: "success",
          });
        }
        setTimeout(fetchControlPanelData, 1000);
      } else {
        enqueueSnackbar(res.data?.message || "Failed to send light response", { variant: "error" });
      }
    } catch (err) {
      console.error("Send light response error:", err);
      enqueueSnackbar("Error sending light response", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  // Build Table Data
  const columns = [
    { Header: "ID", accessor: "id", width: "10%" },
    { Header: "Cam ID", accessor: "cam_id", width: "15%" },
    { Header: "Motion Status", accessor: "motion_status", width: "15%" },
    { Header: "Red LED", accessor: "led_r", width: "10%" },
    { Header: "Green LED", accessor: "led_g", width: "10%" },
    { Header: "White LED", accessor: "led_w", width: "10%" },
    { Header: "Yellow LED", accessor: "led_y", width: "10%" },
    { Header: "State", accessor: "state", width: "10%" },
    { Header: "Last Updated", accessor: "updated_at", width: "10%" },
  ];

  const displayList = allDevices.length > 0 ? allDevices : subDataList;

  const rows = (Array.isArray(displayList) ? displayList : []).map((item) => {
    const devId = String(item.device_id || item.cam_id || item.id);
    const subItem = (Array.isArray(subDataList) ? subDataList : []).find(
      (s) => String(s.cam_id) === devId || (s.data && String(s.data.device) === devId)
    ) || (item.data ? item : null);

    let parsed = {};
    if (subItem) {
      try {
        parsed = typeof subItem.data === "string" ? JSON.parse(subItem.data) : subItem.data || {};
      } catch (e) {
        parsed = {};
      }
    } else {
      const isOn = item.current_status === "1" ? "ON" : "OFF";
      parsed = {
        RED_LED: isOn,
        GREEN_LED: isOn,
        WHITE_LED: isOn,
        YELLOW_LED: isOn,
        State: "ACTIVE",
      };
    }

    const valR = parsed.RED_LED || parsed.LED_R || "OFF";
    const valG = parsed.GREEN_LED || parsed.LED_G || "OFF";
    const valW = parsed.WHITE_LED || parsed.LED_W || "OFF";
    const valY = parsed.YELLOW_LED || parsed.LED_Y || "OFF";
    const itemIsMotion = parsed.event === "motion_detected" || parsed.status === "active" || parsed.motion_detected === true;

    return {
      id: item.id || devId,
      cam_id: (
        <MDTypography
          variant="caption"
          color="info"
          fontWeight="bold"
          style={{ cursor: "pointer" }}
          onClick={() => {
            setCamId(devId);
            if (subItem) {
              populateDeviceDetails(subItem);
            } else {
              const isOn = item.current_status === "1";
              setLedR(isOn);
              setLedG(isOn);
              setLedW(isOn);
              setLedY(isOn);
              setDeviceState("REQUEST_STATE");
            }
          }}
        >
          Device #{devId}
        </MDTypography>
      ),
      motion_status: (
        <Chip
          label={itemIsMotion ? "🚨 MOTION" : "✅ Normal"}
          color={itemIsMotion ? "error" : "success"}
          variant={itemIsMotion ? "filled" : "outlined"}
          size="small"
        />
      ),
      led_r: (
        <Chip
          label={valR}
          color={valR === "ON" ? "error" : "default"}
          size="small"
        />
      ),
      led_g: (
        <Chip
          label={valG}
          color={valG === "ON" ? "success" : "default"}
          size="small"
        />
      ),
      led_w: (
        <Chip
          label={valW}
          color={valW === "ON" ? "info" : "default"}
          size="small"
        />
      ),
      led_y: (
        <Chip
          label={valY}
          color={valY === "ON" ? "warning" : "default"}
          size="small"
        />
      ),
      state: (
        <Chip
          label={parsed.State || "ACTIVE"}
          variant="outlined"
          color="info"
          size="small"
        />
      ),
      updated_at:
        subItem && subItem.updated_at
          ? new Date(subItem.updated_at).toLocaleString()
          : item.updated_at
          ? new Date(item.updated_at).toLocaleString()
          : "-",
    };
  });

  const selectedSubItem = (Array.isArray(subDataList) ? subDataList : []).find(
    (s) => String(s.cam_id) === String(camId) ||
           (s.data && (String(s.data.device) === String(camId) || String(s.data.device) === `VC-${camId}` || String(s.data.device) === `VC-${String(camId).padStart(3, '0')}`))
  );

  let selectedMotionData = {};
  if (selectedSubItem) {
    try {
      selectedMotionData = typeof selectedSubItem.data === "string" ? JSON.parse(selectedSubItem.data) : (selectedSubItem.data || {});
    } catch (e) {
      selectedMotionData = {};
    }
  }

  const isSelectedMotion = selectedMotionData.event === "motion_detected" || selectedMotionData.status === "active" || selectedMotionData.motion_detected === true;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <MDLoading />}
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          {/* Header Card */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white" display="flex" alignItems="center" gap={1}>
                  <LightbulbIcon /> ESP32 Smart Light Control Panel
                </MDTypography>
                <MDButton
                  variant="outlined"
                  color="white"
                  size="small"
                  onClick={fetchControlPanelData}
                  disabled={loading}
                  startIcon={<RefreshIcon />}
                >
                  Refresh Devices
                </MDButton>
              </MDBox>
            </Card>
          </Grid>

          {/* Interactive Control Panel Card */}
          <Grid item xs={12} md={6}>
            <Card style={{ padding: "24px", height: "100%" }}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Send Light Response
              </MDTypography>
              <MDTypography variant="button" color="text" fontWeight="regular" mb={3} display="block">
                Configure LED output states and command payloads to send via MQTT (`Smartlight-{camId}/pub`).
              </MDTypography>

              <Grid container spacing={2}>
                {/* Target Mode Selector: Single Device vs Group */}
                <Grid item xs={12}>
                  <Box display="flex" gap={1} mb={1}>
                    <MDButton
                      variant={targetType === "device" ? "gradient" : "outlined"}
                      color="info"
                      size="small"
                      onClick={() => setTargetType("device")}
                      fullWidth
                    >
                      💡 Single Smartlight
                    </MDButton>
                    <MDButton
                      variant={targetType === "group" ? "gradient" : "outlined"}
                      color="info"
                      size="small"
                      onClick={() => setTargetType("group")}
                      fullWidth
                    >
                      👥 Smartlight Group
                    </MDButton>
                  </Box>
                </Grid>

                {/* Target Selection: Single Smartlight vs Smartlight Group */}
                {targetType === "device" ? (
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="cam-id-label">Target Smartlight (Device ID)</InputLabel>
                      <Select
                        labelId="cam-id-label"
                        value={camId}
                        label="Target Smartlight (Device ID)"
                        onChange={handleDeviceChange}
                        style={{ height: "45px" }}
                      >
                        {allDevices.length > 0 ? (
                          allDevices.map((item) => {
                            const devId = String(item.device_id || item.cam_id || item.id);
                            return (
                              <MenuItem key={item.id || devId} value={devId}>
                                Smartlight #{devId} {item.device_name ? `(${item.device_name})` : ""}
                              </MenuItem>
                            );
                          })
                        ) : Array.isArray(subDataList) && subDataList.length > 0 ? (
                          subDataList.map((item) => (
                            <MenuItem key={item.id} value={String(item.cam_id)}>
                              Smartlight #{item.cam_id}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="10">Smartlight #10 (Default)</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="group-id-label">Target Smartlight Group</InputLabel>
                      <Select
                        labelId="group-id-label"
                        value={selectedGroupId}
                        label="Target Smartlight Group"
                        onChange={(e) => setSelectedGroupId(e.target.value)}
                        style={{ height: "45px" }}
                      >
                        {groupsList.map((grp) => (
                          <MenuItem key={grp.group_id} value={String(grp.group_id)}>
                            {grp.group_name} ({grp.device_count || (grp.devices ? grp.devices.length : 0)} smartlights)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Show member smartlights in selected group */}
                    {(() => {
                      const grp = groupsList.find((g) => String(g.group_id) === String(selectedGroupId));
                      const devs = grp && Array.isArray(grp.devices) ? grp.devices : [];
                      return (
                        <Box mt={1.5} p={1.5} bgcolor="#f8f9fa" borderRadius="8px" border="1px dashed #ced4da">
                          <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                            Smartlights in this group ({devs.length}):
                          </MDTypography>
                          {devs.length === 0 ? (
                            <MDTypography variant="caption" color="error">
                              No smartlights currently in this group.
                            </MDTypography>
                          ) : (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {devs.map((d) => (
                                <Chip
                                  key={d.device_id}
                                  label={`${d.device_name} (#${d.device_id})`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ fontSize: "11px", height: "22px" }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </Grid>
                )}

                {/* State Dropdown */}
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="state-label">Command State</InputLabel>
                    <Select
                      labelId="state-label"
                      value={deviceState}
                      label="Command State"
                      onChange={(e) => setDeviceState(e.target.value)}
                      style={{ height: "45px" }}
                    >
                      <MenuItem value="REQUEST_STATE">REQUEST_STATE</MenuItem>
                      <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                      <MenuItem value="OFF">OFF</MenuItem>
                      <MenuItem value="restarted">RESTART</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* LED Control Toggles */}
                <Grid item xs={12}>
                  <Paper elevation={0} style={{ padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                    <MDTypography variant="caption" fontWeight="bold" color="text" mb={1} display="block">
                      LED OUTPUT SWITCHES
                    </MDTypography>

                    {/* RED LED */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LightbulbIcon style={{ color: ledR ? "#d50000" : "#ccc" }} />
                        <MDTypography variant="button" fontWeight="medium">
                          Red LED (RED_LED)
                        </MDTypography>
                      </Box>
                      <Switch checked={ledR} onChange={(e) => setLedR(e.target.checked)} color="error" />
                    </Box>
                    <Divider style={{ margin: "4px 0" }} />

                    {/* GREEN LED */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LightbulbIcon style={{ color: ledG ? "#00c853" : "#ccc" }} />
                        <MDTypography variant="button" fontWeight="medium">
                          Green LED (GREEN_LED)
                        </MDTypography>
                      </Box>
                      <Switch checked={ledG} onChange={(e) => setLedG(e.target.checked)} color="success" />
                    </Box>
                    <Divider style={{ margin: "4px 0" }} />

                    {/* WHITE LED */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LightbulbIcon style={{ color: ledW ? "#0288d1" : "#ccc" }} />
                        <MDTypography variant="button" fontWeight="medium">
                          White LED (WHITE_LED)
                        </MDTypography>
                      </Box>
                      <Switch checked={ledW} onChange={(e) => setLedW(e.target.checked)} color="info" />
                    </Box>
                    <Divider style={{ margin: "4px 0" }} />

                    {/* YELLOW LED */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LightbulbIcon style={{ color: ledY ? "#ffab00" : "#ccc" }} />
                        <MDTypography variant="button" fontWeight="medium">
                          Yellow LED (YELLOW_LED)
                        </MDTypography>
                      </Box>
                      <Switch checked={ledY} onChange={(e) => setLedY(e.target.checked)} color="warning" />
                    </Box>
                  </Paper>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12} mt={2}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    size="large"
                    onClick={handleSendLightResponse}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  >
                    {submitting ? "Sending MQTT Response..." : "Send Light Response"}
                  </MDButton>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Status Preview Card */}
          <Grid item xs={12} md={6}>
            <Card style={{ padding: "24px", height: "100%" }}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Live Device Telemetry Preview
              </MDTypography>

              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <Paper elevation={0} style={{ padding: "16px", border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    SELECTED DEVICE TOPIC
                  </MDTypography>
                  <MDTypography variant="h5" color="info" fontWeight="bold">
                    Smartlight-{formattedCamId}/pub
                  </MDTypography>
                </Paper>

                {/* Motion Detection Notification Banner for Selected Device */}
                <Paper
                  elevation={0}
                  style={{
                    padding: "16px",
                    borderRadius: "8px",
                    backgroundColor: isSelectedMotion ? "#ffebee" : "#f1f8e9",
                    border: `2px solid ${isSelectedMotion ? "#d32f2f" : "#2e7d32"}`,
                    transition: "all 0.3s ease"
                  }}
                >
                  <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={0.5}>
                    MOTION DETECTION NOTIFICATION (DEVICE #{camId})
                  </MDTypography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {isSelectedMotion ? (
                      <Chip
                        label="🚨 MOTION DETECTED"
                        color="error"
                        style={{ fontWeight: "bold", fontSize: "12px" }}
                      />
                    ) : (
                      <Chip
                        label="✅ Normal (Clear)"
                        color="success"
                        variant="outlined"
                        style={{ fontWeight: "bold", fontSize: "12px" }}
                      />
                    )}
                    <MDTypography variant="button" color="text" fontWeight="medium">
                      Event: {selectedMotionData.event || (isSelectedMotion ? "motion_detected" : "motion_cleared")}
                    </MDTypography>
                  </Box>
                  {selectedMotionData.timestamp && (
                    <MDTypography variant="caption" color="text" fontWeight="regular" display="block" mt={0.5}>
                      Timestamp: {new Date(selectedMotionData.timestamp * (selectedMotionData.timestamp < 10000000000 ? 1000 : 1)).toLocaleString()}
                    </MDTypography>
                  )}
                </Paper>

                <Paper elevation={0} style={{ padding: "16px", border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                  <MDTypography variant="caption" color="text" fontWeight="bold" mb={1} display="block">
                    COMMAND PACKET PAYLOAD
                  </MDTypography>
                  <pre
                    style={{
                      backgroundColor: "#1e1e1e",
                      color: "#4ec9b0",
                      padding: "12px",
                      borderRadius: "6px",
                      fontSize: "13px",
                      overflowX: "auto",
                    }}
                  >
                    {JSON.stringify(
                      {
                        RED_LED: ledR ? "ON" : "OFF",
                        GREEN_LED: ledG ? "ON" : "OFF",
                        WHITE_LED: ledW ? "ON" : "OFF",
                        YELLOW_LED: ledY ? "ON" : "OFF",
                      },
                      null,
                      2
                    )}
                  </pre>
                </Paper>

                {lastUpdated && (
                  <MDTypography variant="caption" color="text" fontWeight="regular">
                    Last Received Telemetry: {lastUpdated}
                  </MDTypography>
                )}
              </Box>
            </Card>
          </Grid>

          {/* Subdata Table Card */}
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="dark" borderRadius="lg" coloredShadow="dark">
                <MDTypography variant="h6" color="white">
                  Active Devices & Telemetry Log (`device_light` / `subdata`)
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={true}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default ControlPanel;
